import React, { useEffect, useRef, useState } from "react";
import api from "../../api/axios";
import { isNativeApp } from "../../native/runtime";
import MobileAppInviteModal from "./MobileAppInviteModal";

const INVITE_ENDPOINTS = Object.freeze({
  ios: "/accounts/ios-app-invite/",
  android: "/accounts/android-app-invite/",
});

export const getMobileWebPlatform = () => {
  if (isNativeApp() || typeof navigator === "undefined") return null;
  const userAgent = String(navigator.userAgent || "");
  if (/Android/i.test(userAgent)) return "android";
  if (/iPhone/i.test(userAgent)) return "ios";
  return null;
};

export const isMobileAppInviteWeb = () => Boolean(getMobileWebPlatform());

export default function MobileAppInviteController({
  userId,
  blocked = false,
  onPriorityChange,
  targetPlatform = null,
}) {
  const detectedPlatform = getMobileWebPlatform();
  const platform = targetPlatform && targetPlatform !== detectedPlatform
    ? null
    : detectedPlatform;
  const endpoint = platform ? INVITE_ENDPOINTS[platform] : null;
  const [eligible, setEligible] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const shownRecordedRef = useRef(false);
  const open = Boolean(platform && loaded && eligible && !blocked);

  useEffect(() => {
    shownRecordedRef.current = false;
    setEligible(false);
    setLoaded(false);

    if (!userId || !endpoint) {
      onPriorityChange?.(false);
      return undefined;
    }

    let cancelled = false;
    onPriorityChange?.(true);
    api.get(endpoint, {
      meta: { suppressGlobalError: true },
    })
      .then(({ data }) => {
        if (!cancelled) setEligible(Boolean(data?.should_show));
      })
      .catch((error) => {
        console.warn(`[MobileAppInvite:${platform}] failed to load invitation state`, error);
        if (!cancelled) setEligible(false);
      })
      .finally(() => {
        if (!cancelled) setLoaded(true);
      });

    return () => {
      cancelled = true;
    };
  }, [endpoint, onPriorityChange, platform, userId]);

  useEffect(() => {
    onPriorityChange?.(Boolean(platform && (!loaded || eligible)));
  }, [eligible, loaded, onPriorityChange, platform]);

  useEffect(() => {
    if (!open || shownRecordedRef.current || !endpoint) return;
    shownRecordedRef.current = true;
    void api.post(
      endpoint,
      { action: "shown" },
      { meta: { suppressGlobalError: true } }
    ).catch((error) => {
      console.warn(`[MobileAppInvite:${platform}] failed to record impression`, error);
    });
  }, [endpoint, open, platform]);

  const dismiss = () => {
    setEligible(false);
    onPriorityChange?.(false);
    if (!endpoint) return;
    void api.post(
      endpoint,
      { action: "later" },
      { meta: { suppressGlobalError: true } }
    ).catch((error) => {
      console.warn(`[MobileAppInvite:${platform}] failed to record dismissal`, error);
    });
  };

  const download = (url) => {
    setEligible(false);
    onPriorityChange?.(false);
    window.open(url, "_blank", "noopener,noreferrer");
    if (!endpoint) return;
    void api.post(
      endpoint,
      { action: "download" },
      { meta: { suppressGlobalError: true } }
    ).catch((error) => {
      console.warn(`[MobileAppInvite:${platform}] failed to record store click`, error);
    });
  };

  return (
    <MobileAppInviteModal
      open={open}
      platform={platform}
      onClose={dismiss}
      onDownload={download}
    />
  );
}
